import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CustomEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Try to read totalCount (preferred), otherwise use itemCount and source
    const data = event.detail.data ?? {};
    const totalCount = typeof data.totalCount === 'number' ? data.totalCount : null;
    const itemCount = typeof data.itemCount === 'number' ? data.itemCount : 0;
    const comingFromProductForm = data.source === 'product-form-component';

    let newCartCount;
    if (totalCount !== null) {
      // Use totalCount if provided
      newCartCount = totalCount;
    } else if (comingFromProductForm) {
      // If only itemCount given and this is from the product form, increment
      newCartCount = this.currentCartCount + itemCount;
    } else {
      // Otherwise, treat itemCount as the new total
      newCartCount = itemCount;
    }

    this.renderCartBubble(newCartCount, animate = true);
  };

  /**
   * Renders the cart bubble.
   * @param {number} cartCount - The number of items in the cart.
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = async (cartCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', cartCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', cartCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', cartCount > 0 && animate);

    this.currentCartCount = cartCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', cartCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(cartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
